import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { stockAdjustmentsApi } from '../../api/warehouse';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { STOCK_ADJUSTMENT_STATUS_LABELS } from './StockAdjustmentsListPage';
import { STOCK_ADJUSTMENT_STAGES } from '../../config/workflow-stages';

export function StockAdjustmentWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const adjustmentId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: adjData, isError } = useQuery({ queryKey: ['stock-adjustment', adjustmentId], queryFn: () => stockAdjustmentsApi.get(adjustmentId) });
  const adjustment = adjData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['stock-adjustment', adjustmentId] });
  const { data: docsData } = useQuery({ queryKey: ['stock-adjustment-documents', adjustmentId], queryFn: () => genericDocumentApi.listFor('stock_adjustment', adjustmentId) });

  const submitMutation = useMutation({
    mutationFn: () => stockAdjustmentsApi.submit(adjustmentId),
    onSuccess: () => { showSuccess('Adjustment submitted for approval.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const approveMutation = useMutation({
    mutationFn: () => stockAdjustmentsApi.approve(adjustmentId),
    onSuccess: () => { showSuccess('Adjustment approved and posted to stock.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const { register: registerReject, handleSubmit: handleRejectSubmit, reset: resetReject } = useForm<{ comment: string }>();
  const rejectMutation = useMutation({
    mutationFn: (comment: string) => stockAdjustmentsApi.reject(adjustmentId, comment),
    onSuccess: () => { showSuccess('Adjustment rejected.'); resetReject(); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this adjustment. Please try refreshing the page.</div></div>;
  if (!adjustment) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={adjustment.adjustment_reference}
        readOnly={false}
        entityType="stock_adjustment"
        entityId={adjustmentId}
        fields={[
          { label: 'Item', value: adjustment.item_name },
          { label: 'Warehouse', value: adjustment.warehouse_name },
          { label: 'Adjustment', value: adjustment.adjustment_quantity },
          { label: 'Status', value: <StatusBadge status={adjustment.status} label={STOCK_ADJUSTMENT_STATUS_LABELS[adjustment.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Details" />
                <dl className="detail-grid">
                  <dt>Current Quantity (at request)</dt><dd>{adjustment.current_quantity}</dd>
                  <dt>Justification</dt><dd>{adjustment.justification}</dd>
                  {adjustment.decision_comment && <><dt>Decision Comment</dt><dd>{adjustment.decision_comment}</dd></>}
                </dl>
                {adjustment.status === 'draft' && <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit for Approval</button>}
              </div>
            ),
          },
          {
            key: 'approval', label: 'Approval', content: (
              <div>
                <SectionHeader title="Approval" />
                {adjustment.status === 'submitted' && (
                  <>
                    <button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve & Post to Stock</button>
                    <form className="inline-form" onSubmit={handleRejectSubmit((d) => rejectMutation.mutate(d.comment))}>
                      <input placeholder="Reason for rejection" {...registerReject('comment', { required: true })} />
                      <button type="submit" disabled={rejectMutation.isPending}>Reject</button>
                    </form>
                    <p className="muted">Note: whoever requested this adjustment cannot approve or reject it themselves.</p>
                  </>
                )}
                {adjustment.status === 'posted' && <p className="muted">This adjustment has been approved and posted to the stock ledger.</p>}
                {adjustment.status === 'rejected' && <p className="muted">This adjustment was rejected.</p>}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('stock_adjustment', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('stock_adjustment', adjustmentId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('stock_adjustment', documentId)}
                queryKeyToInvalidate={['stock-adjustment-documents', adjustmentId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="stock_adjustment" entityId={adjustmentId} actionStageMap={STOCK_ADJUSTMENT_STAGES} statusLabels={STOCK_ADJUSTMENT_STATUS_LABELS} />
                <CommentsThread entityType="stock_adjustment" entityId={adjustmentId} section="warehouse" heading="Comments for this Adjustment" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
